import { ParsedPolicyError } from 'api/requests/time-registration-policy-requests';
import {
  TIME_REGISTRATION_POLICY__AUTH_OPEN_LIST,
  TIME_REGISTRATION_POLICY__AUTH_PIN,
  TIME_REGISTRATION_POLICY__AUTH_PIN_APPROVAL,
  TIME_REGISTRATION_POLICY__CONFIRM_KIOSK_IMMEDIATE,
  TIME_REGISTRATION_POLICY__CONFIRM_LOSING_ONE,
  TIME_REGISTRATION_POLICY__CONFIRM_LOSING_OTHER,
  TIME_REGISTRATION_POLICY__CONFIRM_NO_COMPANY,
  TIME_REGISTRATION_POLICY__CONFIRM_NO_OVERRULE_LOST,
  TIME_REGISTRATION_POLICY__CONFIRM_RECEIVING_ONE,
  TIME_REGISTRATION_POLICY__CONFIRM_RECEIVING_OTHER,
  TIME_REGISTRATION_POLICY__CONFIRM_TITLE_CHANGE,
  TIME_REGISTRATION_POLICY__CONFIRM_TITLE_LOCK,
  TIME_REGISTRATION_POLICY__CONFIRM_TITLE_UNLOCK,
  TIME_REGISTRATION_POLICY__CONFIRM_UNLOCK,
  TIME_REGISTRATION_POLICY__INVALID_MAXIMUM_NUMBER_OF_BREAKS,
  TIME_REGISTRATION_POLICY__INVALID_MINUTES,
  TIME_REGISTRATION_POLICY__INVALID_ON_SITE_RADIUS,
  TIME_REGISTRATION_POLICY__INVALID_ROUNDING_INTERVAL,
  TIME_REGISTRATION_POLICY__REQUIRED_FIELD,
  TIME_REGISTRATION_POLICY__VALUE_DISABLED,
  TIME_REGISTRATION_POLICY__VALUE_ENABLED,
  TIME_REGISTRATION_POLICY__VALUE_LOCATION_NOT_REQUIRED,
  TIME_REGISTRATION_POLICY__VALUE_LOCATION_REQUIRED,
  TIME_REGISTRATION_POLICY__VALUE_OFF,
  TIME_REGISTRATION_POLICY__VALUE_OVERWORK,
  TIME_REGISTRATION_POLICY__VALUE_ROUNDING,
  TIME_REGISTRATION_POLICY__VALUE_WINDOW,
  TIME_REGISTRATION_SETTINGS__ROUND_DOWN,
  TIME_REGISTRATION_SETTINGS__ROUND_UP,
} from 'translations/constants';
import { KioskAuthenticationMode } from 'types/company/kiosk/IKioskSettings';
import { TimeRegistrationRoundingDirection } from 'types/company/time-registration/ITimeRegistrationSettings';
import {
  FieldError,
  FieldErrors,
  ImpactDto,
  PolicySaveResult,
  ProposedUnitState,
  UnitState,
  UnitValue,
  UnitValueField,
} from 'types/time-registration-policy/ITimeRegistrationPolicy';
import { UnitDefinition, UnitKind } from './units';

type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface ConfirmationLine {
  key: string;
  params?: Record<string, number>;
}

export const AUTH_MODE_LABEL_KEYS: Record<KioskAuthenticationMode, string> = {
  [KioskAuthenticationMode.OpenList]: TIME_REGISTRATION_POLICY__AUTH_OPEN_LIST,
  [KioskAuthenticationMode.Pin]: TIME_REGISTRATION_POLICY__AUTH_PIN,
  [KioskAuthenticationMode.PinPlusPersonalDeviceApproval]: TIME_REGISTRATION_POLICY__AUTH_PIN_APPROVAL,
};

export const DIRECTION_LABEL_KEYS: Record<TimeRegistrationRoundingDirection, string> = {
  [TimeRegistrationRoundingDirection.RoundUp]: TIME_REGISTRATION_SETTINGS__ROUND_UP,
  [TimeRegistrationRoundingDirection.RoundDown]: TIME_REGISTRATION_SETTINGS__ROUND_DOWN,
};

export const formatUnitValue = (def: UnitDefinition, value: UnitValue, t: Translate): string => {
  switch (def.kind) {
    case 'toggle':
      return t(value.enabled ? TIME_REGISTRATION_POLICY__VALUE_ENABLED : TIME_REGISTRATION_POLICY__VALUE_DISABLED);
    case 'window':
      return t(TIME_REGISTRATION_POLICY__VALUE_WINDOW, { before: value.beforeMinutes, after: value.afterMinutes });
    case 'number':
      return String(value.maximum ?? '');
    case 'authMode':
      return value.authenticationMode ? t(AUTH_MODE_LABEL_KEYS[value.authenticationMode]) : '';
    case 'rounding':
      return value.enabled
        ? t(TIME_REGISTRATION_POLICY__VALUE_ROUNDING, {
            start: value.startIntervalMinutes,
            startDirection: value.startDirection ? t(DIRECTION_LABEL_KEYS[value.startDirection]) : '',
            end: value.endIntervalMinutes,
            endDirection: value.endDirection ? t(DIRECTION_LABEL_KEYS[value.endDirection]) : '',
          })
        : t(TIME_REGISTRATION_POLICY__VALUE_OFF);
    case 'overwork':
      return value.enabled
        ? t(TIME_REGISTRATION_POLICY__VALUE_OVERWORK, { max: value.maxOverworkMinutes })
        : t(TIME_REGISTRATION_POLICY__VALUE_OFF);
    case 'location':
      return value.enabled
        ? t(TIME_REGISTRATION_POLICY__VALUE_LOCATION_REQUIRED, { radius: value.onSiteRadiusMeters })
        : t(TIME_REGISTRATION_POLICY__VALUE_LOCATION_NOT_REQUIRED);
    default:
      return '';
  }
};

const isWholeInRange = (value: number | null | undefined, min: number, max: number): boolean =>
  value !== null && value !== undefined && Number.isInteger(value) && value >= min && value <= max;

const rangeError = (key: string, min: number, max: number): FieldError => ({ key, params: { min, max } });

export const validateUnitValue = (def: UnitDefinition, value: UnitValue): FieldErrors => {
  const errors: FieldErrors = {};
  const checkRange = (field: UnitValueField, key: string, min: number, max: number) => {
    if (!isWholeInRange(value[field] as number, min, max)) errors[field] = rangeError(key, min, max);
  };
  switch (def.kind) {
    case 'window':
      checkRange('beforeMinutes', TIME_REGISTRATION_POLICY__INVALID_MINUTES, 0, 1440);
      checkRange('afterMinutes', TIME_REGISTRATION_POLICY__INVALID_MINUTES, 0, 1440);
      break;
    case 'number':
      checkRange('maximum', TIME_REGISTRATION_POLICY__INVALID_MAXIMUM_NUMBER_OF_BREAKS, 0, 10);
      break;
    case 'authMode':
      if (!value.authenticationMode) errors.authenticationMode = { key: TIME_REGISTRATION_POLICY__REQUIRED_FIELD };
      break;
    case 'overwork':
      if (value.enabled) checkRange('maxOverworkMinutes', TIME_REGISTRATION_POLICY__INVALID_MINUTES, 0, 1440);
      break;
    case 'location':
      if (value.enabled) checkRange('onSiteRadiusMeters', TIME_REGISTRATION_POLICY__INVALID_ON_SITE_RADIUS, 1, 10000);
      break;
    case 'rounding':
      if (value.enabled) {
        if (!value.startDirection) errors.startDirection = { key: TIME_REGISTRATION_POLICY__REQUIRED_FIELD };
        if (!value.endDirection) errors.endDirection = { key: TIME_REGISTRATION_POLICY__REQUIRED_FIELD };
        checkRange('startIntervalMinutes', TIME_REGISTRATION_POLICY__INVALID_ROUNDING_INTERVAL, 1, 1440);
        checkRange('endIntervalMinutes', TIME_REGISTRATION_POLICY__INVALID_ROUNDING_INTERVAL, 1, 1440);
      }
      break;
    default:
      break;
  }
  return errors;
};

export const toProposed = (state: UnitState): ProposedUnitState => ({
  value: state.value,
  overrulable: state.overrulable,
});

const VALUE_FIELDS_BY_KIND: Record<UnitKind, UnitValueField[]> = {
  toggle: ['enabled'],
  window: ['beforeMinutes', 'afterMinutes'],
  number: ['maximum'],
  authMode: ['authenticationMode'],
  overwork: ['enabled', 'maxOverworkMinutes'],
  location: ['enabled', 'onSiteRadiusMeters'],
  rounding: ['enabled', 'startIntervalMinutes', 'startDirection', 'endIntervalMinutes', 'endDirection'],
};

const SWITCHABLE_KINDS: UnitKind[] = ['rounding', 'overwork', 'location'];

export const isSwitchableKind = (kind: UnitKind): boolean => SWITCHABLE_KINDS.includes(kind);

export const pickUnitFields = (def: UnitDefinition, value: UnitValue): UnitValue =>
  VALUE_FIELDS_BY_KIND[def.kind].reduce<UnitValue>((acc, field) => ({ ...acc, [field]: value[field] ?? null }), {});

export const hasValueChanged = (def: UnitDefinition, stored: UnitValue, proposed: UnitValue): boolean => {
  const fields: UnitValueField[] =
    isSwitchableKind(def.kind) && !proposed.enabled ? ['enabled'] : VALUE_FIELDS_BY_KIND[def.kind];
  return fields.some(field => (stored[field] ?? null) !== (proposed[field] ?? null));
};

export const hasUnitChanged = (def: UnitDefinition, stored: UnitState, proposed: ProposedUnitState): boolean =>
  stored.overrulable !== proposed.overrulable || hasValueChanged(def, stored.value, proposed.value);

export const buildConfirmation = (
  def: UnitDefinition,
  impact: ImpactDto,
  valueChanged: boolean,
): { titleKey: string; lines: ConfirmationLine[] } => {
  let titleKey = TIME_REGISTRATION_POLICY__CONFIRM_TITLE_CHANGE;
  if (impact.isLock) titleKey = TIME_REGISTRATION_POLICY__CONFIRM_TITLE_LOCK;
  else if (impact.isUnlock) titleKey = TIME_REGISTRATION_POLICY__CONFIRM_TITLE_UNLOCK;

  const lines: ConfirmationLine[] = [];
  const noneAffected = impact.companiesReceivingChange === 0 && impact.companiesLosingOverrule === 0;
  if (noneAffected && !impact.isUnlock) {
    lines.push({ key: TIME_REGISTRATION_POLICY__CONFIRM_NO_COMPANY });
  } else if (valueChanged || impact.isLock) {
    const count = impact.companiesReceivingChange;
    lines.push({
      key:
        count === 1
          ? TIME_REGISTRATION_POLICY__CONFIRM_RECEIVING_ONE
          : TIME_REGISTRATION_POLICY__CONFIRM_RECEIVING_OTHER,
      params: { count },
    });
  }
  if (impact.isLock && !noneAffected) {
    const lost = impact.companiesLosingOverrule;
    if (lost === 0) {
      lines.push({ key: TIME_REGISTRATION_POLICY__CONFIRM_NO_OVERRULE_LOST });
    } else {
      lines.push({
        key: lost === 1 ? TIME_REGISTRATION_POLICY__CONFIRM_LOSING_ONE : TIME_REGISTRATION_POLICY__CONFIRM_LOSING_OTHER,
        params: { count: lost },
      });
    }
  }
  if (impact.isUnlock) lines.push({ key: TIME_REGISTRATION_POLICY__CONFIRM_UNLOCK });
  if (def.kiosk) lines.push({ key: TIME_REGISTRATION_POLICY__CONFIRM_KIOSK_IMMEDIATE });
  return { titleKey, lines };
};

export const mapSaveError = (parsed: ParsedPolicyError): PolicySaveResult => {
  if (parsed.timedOut) return { status: 'timeout' };
  if (parsed.status === 409) return { status: 'conflict' };
  if (parsed.status === 400 && parsed.key && parsed.details.field) {
    const { field, min, max } = parsed.details;
    const params = min !== undefined && max !== undefined ? { min, max } : undefined;
    return { status: 'invalid', fieldErrors: { [field]: { key: parsed.key, params } } };
  }
  return { status: 'error' };
};
